import * as React from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useForm } from 'react-hook-form';
import { Modal, ModalContent } from '@restworld/ui-ds-base';

import { AddressFieldType } from '@restworld/data-services';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import RestaurantOnboarding from './restaurant-onboarding';
import { RestaurantOnboardingFooter } from './restaurant-onboarding-footer';
import { googleAddresInputValueAtom } from './google-address-input-result';
import { shouldShowOnboardingFooter } from './should-show-onboarding-footer';
import { cn } from '@restworld/utils-common';
import {
  useRestaurantOnboardingFormReset,
  useRestaurantOnboardingResetActions,
} from './restaurant-onboarding-modal.hooks';

export type RestaurantOnboardingStage =
  | 'intro'
  | 'select_restaurant_from_google'
  | 'magic_ai_loading'
  | 'restaurant_onboarding_form'
  | 'restaurant_owner_onboarding_form'
  | 'restaurant_onboarding_successfull'
  | 'restaurant_already_exists'
  | 'contact_already_exists'
  | 'otp_verification'
  | 'not_horeca';

export const restaurantOnboardingFormDefaultValues = {
  restaurant_name: '',
  address: '',
  brief_description: '',
  covers_per_service: '',
  instagram_url: '',
  facebook_url: '',
  website_url: '',
  the_fork_url: '',
  opening_hours: '',
  number_of_staffs: '',
  name: '',
  surname: '',
  email: '',
  phone_number: '',
  privacy_policy: false,
  terms_and_conditions: false,
  vexatious_clauses_acceptance: false,
};
// Atoms
export const activeRestaurantOnboardingStateAtom = atom<RestaurantOnboardingStage>('select_restaurant_from_google');
export const restaurantOnboardingModalVisibleAtom = atom(false);
export const isManualEntryAtom = atom(false);
export const manualAddressFieldsAtom = atom<Partial<AddressFieldType>>({});
// Latest Google Places `status` observed (OK, ZERO_RESULTS, OVER_QUERY_LIMIT, REQUEST_DENIED,
// INVALID_REQUEST, UNKNOWN_ERROR, NOT_FOUND, ...). Empty string means no status seen yet.
// (Using an empty-string sentinel rather than `null` because this tsconfig has
// `strict: false`, which makes `atom<string | null>(null)` resolve to the read-only overload.)
export const googlePlacesLastStatusAtom = atom('');
// Set to true when an `employer_onboarding_google_search_no_result` event was emitted
// during the current restaurant-creation step session. Read at manual form submit to
// fill `had_prior_no_result`.
export const googleSearchNoResultSeenAtom = atom(false);

// Main Component modal for authenticated user
const RestaurantOnboardingModal: React.FunctionComponent = () => {
  const restaurantOnboardingModalVisible = useAtomValue(restaurantOnboardingModalVisibleAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);

  const methods = useForm<RestaurantOnboardingPayload>({ defaultValues: restaurantOnboardingFormDefaultValues });

  const { resetState, handleModalClose } = useRestaurantOnboardingResetActions();
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);

  const handleBack = React.useCallback(() => {
    if (activeUI === 'restaurant_already_exists' || activeUI === 'restaurant_onboarding_form') {
      setActiveUI('select_restaurant_from_google');
      setIsManualEntry(false);
      setManualAddressFields({});
    } else {
      resetState();
    }
  }, [activeUI, resetState, setActiveUI, setIsManualEntry, setManualAddressFields]);
  useRestaurantOnboardingFormReset({ methods });

  const isRestaurantOnboardingSuccessful = activeUI === 'restaurant_onboarding_successfull';
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const shouldShowFooter = React.useMemo(
    () => shouldShowOnboardingFooter(activeUI, googleInputAddressValue.length > 0),
    [activeUI, googleInputAddressValue]
  );
  const modalContentClassName = React.useMemo(() => {
    const classes = ['overflow-x-hidden'];
    if (isRestaurantOnboardingSuccessful) classes.push('bg-purple-50');
    return classes.join(' ');
  }, [isRestaurantOnboardingSuccessful]);

  return (
    <Modal open={restaurantOnboardingModalVisible} onOpenChange={(v) => !v && handleModalClose()}>
      <ModalContent
        size="3xl"
        showBackButton={activeUI === 'restaurant_onboarding_form' || activeUI === 'restaurant_already_exists'}
        onBack={handleBack}
        className={modalContentClassName}
        footer={shouldShowFooter ? <RestaurantOnboardingFooter methods={methods} /> : undefined}
        noPadding={isRestaurantOnboardingSuccessful}
        hideHeader={isRestaurantOnboardingSuccessful}
      >
        <RestaurantOnboardingWrapperStyled>
          <RestaurantOnboarding methods={{ ...methods }} />
        </RestaurantOnboardingWrapperStyled>
      </ModalContent>
    </Modal>
  );
};

export default RestaurantOnboardingModal;
const RestaurantOnboardingWrapperStyled = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full', className)} {...props} />
);
